// "Precisa da sua atenção": fila única, do mais urgente ao menos urgente, só com itens reais (inbox + pendências de
// autorização, tenancy e caixas compartilhadas). Sem itens → estado vazio honesto; erro → mensagem, nunca "zero".
import { Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { type AttentionItem, AttentionList, ChartCard } from '@giyus/patterns';
import { Alert, AlertDescription, EmptyState } from '@giyus/ui';

import { useDateFormatter } from '../lib/dates/useDateFormatter';
import {
  type HomeInboxItem,
  filterInboxForTenant,
  inboxRank,
  inboxTone,
  sortByUrgency,
} from './homeData';
import type { HomeScope, HomeSignals } from './useHomeSignals';

export const ATTENTION_LIMIT = 8;
export const LINK_CLASS =
  'font-medium text-link underline-offset-4 hover:underline focus-visible:rounded-sm';

interface RankedAttention extends AttentionItem {
  readonly rank: number;
  readonly dueAt: string | null;
}

export interface AttentionPanelProps {
  readonly scope: HomeScope;
  /** Rotas-base já resolvidas (`/network` ou `/t/CODE`) e tenant ativo (filtra o inbox). */
  readonly basePath: string;
  readonly tenantId: string | null;
  readonly signals: HomeSignals;
}

export function AttentionPanel({ scope, basePath, tenantId, signals }: AttentionPanelProps) {
  const { t } = useTranslation(['shell', 'workflow', 'common']);
  const dates = useDateFormatter();
  const text = (key: string, values?: Record<string, unknown>): string =>
    t(`shell:home.dashboard.attention.${key}`, values as never) as unknown as string;

  // Só valem as fontes ligadas para este usuário/escopo: o cache pode guardar dado/erro de outro escopo (voltar no navegador).
  const { access } = signals;
  const sources = [
    signals.inbox,
    ...(access.breakGlass ? [signals.breakGlass] : []),
    ...(access.twoKey ? [signals.twoKey] : []),
    ...(access.accessReviews ? [signals.accessReviews] : []),
    ...(access.delegations ? [signals.delegations] : []),
    ...(access.tenants ? [signals.tenants] : []),
    ...(access.studies ? [signals.studies] : []),
  ];
  // Carrega até a última fonte ligada responder (senão o "nada pendente" apareceria cedo demais).
  const loading = sources.some((source) => source.isLoading);
  // Erro de qualquer fonte nunca vira "zero": mostra aviso e suprime o estado vazio.
  const inboxFailed = signals.inbox.isError;
  const partialFailed = !inboxFailed && sources.some((source) => source.isError);

  const inboxItems: readonly HomeInboxItem[] =
    scope === 'tenant' && tenantId
      ? filterInboxForTenant(signals.inbox.data ?? [], tenantId)
      : (signals.inbox.data ?? []);

  const items: RankedAttention[] = [];

  for (const entry of inboxItems) {
    const rank = inboxRank(entry);
    const typeLabel =
      entry.kind === 'task'
        ? t(`workflow:taskTypes.${entry.typeCode}.name`, { defaultValue: entry.typeCode })
        : entry.kind === 'exception'
          ? t(`workflow:exceptionTypes.${entry.typeCode}.name`, { defaultValue: entry.typeCode })
          : entry.typeCode;
    items.push({
      id: `inbox-${entry.kind}-${entry.id}`,
      rank,
      dueAt: entry.dueAt,
      tone: inboxTone(entry),
      toneLabel: entry.overdue ? text('overdue') : rank >= 3 ? text('critical') : text('pending'),
      title: (
        <Link to={`/me/inbox/${entry.kind}/${entry.id}`} className={LINK_CLASS}>
          {entry.subject ?? (typeLabel || t(`workflow:kinds.${entry.kind}`))}
        </Link>
      ),
      meta: `${t(`workflow:kinds.${entry.kind}`)} · ${
        entry.dueAt ? text('dueAt', { when: dates.dateTime(entry.dueAt) }) : text('noDueDate')
      }`,
    });
  }

  const breakGlass = access.breakGlass ? (signals.breakGlass.data ?? []) : [];
  if (breakGlass.length > 0) {
    const overdue = breakGlass.filter((event) => event.reviewOverdue).length;
    items.push({
      id: 'break-glass',
      rank: overdue > 0 ? 4 : 3,
      dueAt: null,
      tone: overdue > 0 ? 'danger' : 'warning',
      toneLabel: overdue > 0 ? text('overdue') : text('pending'),
      title: (
        <Link to={`${basePath}/authz/break-glass`} className={LINK_CLASS}>
          {text('breakGlass', { count: breakGlass.length })}
        </Link>
      ),
      ...(overdue > 0 ? { meta: text('breakGlassOverdue', { count: overdue }) } : {}),
    });
  }

  const twoKey = access.twoKey ? (signals.twoKey.data ?? []) : [];
  if (twoKey.length > 0) {
    items.push({
      id: 'two-key',
      rank: 3,
      dueAt: twoKey.map((a) => a.expiresAt).sort()[0] ?? null,
      tone: 'warning',
      toneLabel: text('pending'),
      title: (
        <Link to="/me/two-key" className={LINK_CLASS}>
          {text('twoKey', { count: twoKey.length })}
        </Link>
      ),
    });
  }

  const reviews = (access.accessReviews ? (signals.accessReviews.data ?? []) : []).filter(
    (item) => item.decidedAt === null,
  );
  if (reviews.length > 0) {
    items.push({
      id: 'access-reviews',
      rank: 2,
      dueAt: null,
      tone: 'warning',
      toneLabel: text('pending'),
      title: (
        <Link to="/me/access-reviews" className={LINK_CLASS}>
          {text('accessReview', { count: reviews.length })}
        </Link>
      ),
    });
  }

  const delegations = access.delegations ? (signals.delegations.data ?? []) : [];
  if (delegations.length > 0) {
    items.push({
      id: 'delegations',
      rank: 2,
      dueAt: null,
      tone: 'warning',
      toneLabel: text('pending'),
      title: (
        <Link to="/me/delegations" className={LINK_CLASS}>
          {text('delegation', { count: delegations.length })}
        </Link>
      ),
    });
  }

  const bootstrapPending = (access.tenants ? (signals.tenants.data?.items ?? []) : []).filter(
    (tenant) => tenant.bootstrapPending,
  );
  if (bootstrapPending.length > 0) {
    items.push({
      id: 'bootstrap',
      rank: 2,
      dueAt: null,
      tone: 'warning',
      toneLabel: text('pending'),
      title: (
        <Link to={`${basePath}/tenants`} className={LINK_CLASS}>
          {text('bootstrap', { count: bootstrapPending.length })}
        </Link>
      ),
      meta: bootstrapPending.map((tenant) => tenant.displayName).join(', '),
    });
  }

  const studies = access.studies ? signals.studies.data : undefined;
  const unassigned = Number(studies?.unassignedMailboxMessages ?? 0);
  if (unassigned > 0) {
    const oldest = studies?.oldestUnassignedReceivedAtIso ?? null;
    items.push({
      id: 'mailbox',
      rank: 1,
      dueAt: null,
      tone: 'info',
      toneLabel: text('pending'),
      title: (
        <Link to={`${basePath}/shared-mailboxes`} className={LINK_CLASS}>
          {text('mailbox', { count: unassigned })}
        </Link>
      ),
      ...(oldest ? { meta: text('mailboxOldest', { when: dates.date(oldest) }) } : {}),
    });
  }

  const sorted = sortByUrgency(items);
  const visible = sorted.slice(0, ATTENTION_LIMIT);
  // No tenant os contadores somam todos os contextos; aqui só interessam os itens deste contexto.
  const totalInbox =
    scope === 'tenant' ? inboxItems.length : (signals.counters.data?.total ?? inboxItems.length);
  const shownInbox = visible.filter((item) => item.id.startsWith('inbox-')).length;
  const moreInInbox = Math.max(0, totalInbox - shownInbox);
  const failed = inboxFailed || partialFailed;

  return (
    <section aria-labelledby="home-attention-title" data-testid="home-attention">
      <ChartCard
        title={<span id="home-attention-title">{text('title')}</span>}
        description={text('description')}
        loading={loading}
        loadingLabel={t('common:state.loading')}
        action={
          <Link to="/me/inbox" className={LINK_CLASS}>
            {text('viewAll')}
          </Link>
        }
        emptyState={
          !loading && visible.length === 0 && !failed ? (
            <EmptyState
              icon={<Inbox />}
              title={text('empty')}
              description={text('emptyHint')}
              headingLevel="h3"
            />
          ) : undefined
        }
      >
        {failed ? (
          <Alert variant="destructive">
            <AlertDescription>{text(inboxFailed ? 'error' : 'errorPartial')}</AlertDescription>
          </Alert>
        ) : null}
        {visible.length > 0 ? <AttentionList items={visible} label={text('listLabel')} /> : null}
        {moreInInbox > 0 ? (
          <p className="text-body-sm text-muted-foreground">
            <Link to="/me/inbox" className={LINK_CLASS}>
              {text('moreInInbox', { count: moreInInbox })}
            </Link>
          </p>
        ) : null}
      </ChartCard>
    </section>
  );
}
